import { ReportingTeacherUiHeader } from 'teacher-ui-client/components/reporting/teacher-ui-header'
import { ReportingDetailedControls } from 'teacher-ui-client/components/reporting/detailed-controls'
import { ReportingNoDataMessageIf } from 'teacher-ui-client/components/reporting/no-data-message-if'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import Tooltip from 'teacher-ui-client/components/tooltip'
import { array, concat } from '@ember/helper'
import { ExportType } from 'teacher-ui-client/components/reporting/export-select'
import type UiScopeMathsFluencySprintsTeacherRoute from 'teacher-ui-client/reporting/ui-scope/maths/fluency-sprints/teacher/route'
import { not } from 'ember-truth-helpers'
import { camelize } from '@ember/string'
import { StatBoxesFluencySprints } from 'teacher-ui-client/components/stat-boxes/fluency-sprints'
import { t } from 'ember-intl'
import { TabGroupComponent } from 'teacher-ui-client/components/tab-group'
import { ContentTile } from 'teacher-ui-client/components/tiles/content'
import IsSubco from 'teacher-ui-client/helpers/is-subco'
import { themeKey } from 'teacher-ui-client/helpers/theme-key'
import { TAB_TYPE_KEYS } from 'teacher-ui-client/components/tab-group/tab-types'
import RouteTemplate from 'ember-route-template'
import Component from '@glimmer/component'
import { service } from '@ember/service'
import type RouterService from '@ember/routing/router-service'

interface Signature {
  Args: {
    model: ModelFor<UiScopeMathsFluencySprintsTeacherRoute>
  }
}
export class UiScopeMathsFluencySprintsTeacherPage extends Component<Signature> {
  @service router!: RouterService

  readonly teachersRoute = 'reporting.ui-scope.maths.fluency-sprints.teacher.teachers'
  readonly studentResultsRoute = 'reporting.ui-scope.maths.fluency-sprints.teacher.student-results'
  readonly studentResultsOverviewRoute = 'reporting.ui-scope.maths.fluency-sprints.teacher.student-results-overview'

  get isTeachersRoute() {
    return this.router.currentRouteName === this.teachersRoute
  }

  <template>
    <ReportingTeacherUiHeader @title={{t "reporting.pageTitles.fluencySprintsOverview"}} />
    <ReportingDetailedControls
      @students={{@model.students}}
      @exportOptions={{array ExportType.CSV ExportType.print}}
      @isDataPresent={{Boolean @model.mentalMinuteSummary}}
      @showScopePicker={{not this.isTeachersRoute}}
      @showDatePicker={{true}}
      @studentRouteName="reporting.ui-scope.maths.fluency-sprints.student.table"
    />
    <ReportingNoDataMessageIf @on={{not @model.mentalMinuteSummary}}>
      <div class="page-break-after-always rounded-md p-3 shadow-md print:shadow-none">
        <div class="text-right print:hidden">
          <span>
            <PhosphorIcon @icon="phosphor-info-fill" class="text-dusty-black-500 h-3 w-3 cursor-pointer" />
            <Tooltip @text={{t "reporting.maths.fluencySprints.statBoxes.tooltip.teacher"}} />
          </span>
        </div>
        {{#if @model.mentalMinuteSummary}}
          <StatBoxesFluencySprints @data={{@model.mentalMinuteSummary}} @dataScope={{camelize @model.uiScope.scope}} />
        {{/if}}
      </div>

      <div class="mt-6">
        <TabGroupComponent @theme={{themeKey "maths"}} @tabType={{TAB_TYPE_KEYS.shadow}} as |tabGroup|>
          {{#if (IsSubco)}}
            <tabGroup.linkToTab @route={{this.teachersRoute}} data-test-fluency-teachers-link>
              {{t "reporting.maths.fluencySprints.teacher.tables.titles.teachers"}}
            </tabGroup.linkToTab>
          {{/if}}

          <tabGroup.linkToTab @route={{this.studentResultsRoute}} data-test-fluency-student-results-link>
            {{#if (IsSubco)}}
              {{t "reporting.maths.fluencySprints.teacher.tables.titles.resultsPerStudentSubco"}}
            {{else}}
              {{t "reporting.maths.fluencySprints.teacher.tables.titles.resultsPerStudent"}}
            {{/if}}
          </tabGroup.linkToTab>

          <tabGroup.linkToTab
            @route={{this.studentResultsOverviewRoute}}
            data-test-fluency-student-results-overview-link
          >
            {{#if (IsSubco)}}
              {{t
                (concat
                  "reporting.maths.fluencySprints.teacher.tables.titles.studentResultsOverviewSubco."
                  (camelize @model.uiScope.scope)
                )
              }}
            {{else}}
              {{t
                (concat
                  "reporting.maths.fluencySprints.teacher.tables.titles.studentResultsOverview."
                  (camelize @model.uiScope.scope)
                )
              }}
            {{/if}}
          </tabGroup.linkToTab>
        </TabGroupComponent>

        <ContentTile class="relative rounded-tl-none print:border-none">
          <div class="bg-ms-green-100 h-3"></div>
          <div class="overflow-x-auto print:overflow-x-hidden">
            {{outlet}}
          </div>
        </ContentTile>
      </div>

    </ReportingNoDataMessageIf>
  </template>
}

export default RouteTemplate(UiScopeMathsFluencySprintsTeacherPage)
